import { useMemo, useState } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';

// Our components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import TextInput from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import PropertyTypeDropdown from 'components/Dropdown/mortgage/PropertyTypeDropdown';
import OutstandingBalanceInput from 'components/Client/Onboarding/OutstandingBalanceInput';
import RateInput from 'components/Input/RateInput';
import SoraTextField from 'components/Input/SoraTextField';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';
import { NUMBER } from 'components/Input/Types';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Utils
import {
	clearFormValues,
	dollarFormatter,
	isSubmissionReady
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';
import getDwellingType from 'shared/utils/mortgage/getDwellingType';
import getPropertyUse from 'shared/utils/mortgage/getPropertyUse';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	EMPLOYMENT_STATUS_OPTIONS,
	CASHOUT_OFFER_TYPE,
	MORTGAGE,
	MORTGAGE_LOAN_TENURE_OPTIONS,
	TYPES_OF_HOME_PROPERTIES
} from 'shared/constants';

function getFinalAmount(
	isOutstandingBalancePresent,
	isLoanAmountPresent,
	outstandingBalance,
	loanAmount
) {
	if (isOutstandingBalancePresent && isLoanAmountPresent) {
		return +loanAmount + +outstandingBalance;
	}

	if (isOutstandingBalancePresent) {
		return +outstandingBalance;
	}

	if (isLoanAmountPresent) {
		return +loanAmount;
	}

	return 0;
}

function CashOutRefiForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	clientMortgageInfo,
	postSuccessEvent,
	disableAutoComplete,
	setDisableAutoComplete
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');
	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry, isError: sendFormDataError } =
		saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Build full name
	const {
		clientFullName,
		clientId,
		firstName,
		lastName,
		annualIncome,
		creditScoreValue
	} = clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	const {
		currentLoanInterestRateValue,
		currentOutstandingBalance,
		currentLoanRemainingTermValue,
		refinancingTradelineId
	} = financialData;

	const {
		addressLine1Value,
		addressLine2Value,
		cityValue,
		dwellingType,
		estimatedValue,
		primaryUseValue,
		stateValue,
		zipCodeValue
	} = clientMortgageInfo;

	// Pre-populated state items
	const [loanAmount, setLoanAmount] = useState('');
	const [employmentStatus, setEmploymentStatus] = useState('Employed');
	const [propertyType, setPropertyType] = useState('');
	const [primaryUse, setPrimaryUse] = useState(primaryUseValue || '');
	const [currentInterestRate, setCurrentInterestRate] = useState('');
	const [remainingTerm, setRemainingTerm] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [estimatedPurchaseValue, setEstimatedPurchaseValue] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState('');

	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const formData = [
		isClientNameProvided,
		propertyType,
		primaryUse,
		outstandingBalance,
		estimatedPurchaseValue,
		currentInterestRate,
		ownershipTenure,
		city,
		state,
		zipCode
	];

	const loanRequestDetailsRequiredFields = [
		propertyType,
		primaryUse,
		outstandingBalance,
		remainingTerm,
		estimatedPurchaseValue,
		currentInterestRate,
		ownershipTenure,
		city,
		state,
		zipCode
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl ? loanRequestDetailsRequiredFields : formData
	);

	usePrepopulateState(
		[
			{
				setState: setPrimaryUse,
				value: getPropertyUse(primaryUseValue)
			},
			{
				setState: setEstimatedPurchaseValue,
				value: estimatedValue
			},
			{
				setState: setCity,
				value: cityValue
			},
			{
				setState,
				value: `${stateValue}`
			},
			{
				setState: setAddressLine1,
				value: addressLine1Value
			},
			{
				setState: setAddressLine2,
				value: addressLine2Value
			},
			{
				setState: setZipCode,
				value: zipCodeValue
			},
			{
				setState: setCurrentInterestRate,
				value: `${currentLoanInterestRateValue}`
			},
			{
				setState: setOutstandingBalance,
				value: `${currentOutstandingBalance}`
			},
			{
				setState: setRemainingTerm,
				value: Math.floor(currentLoanRemainingTermValue / 12)
			},
			{
				setState: setPropertyType,
				value: getDwellingType(dwellingType)
			}
		],

		[
			currentLoanRemainingTermValue,
			cityValue,
			refinancingTradelineId,
			currentOutstandingBalance,
			currentLoanInterestRateValue,
			estimatedValue,
			primaryUseValue,
			stateValue,
			zipCodeValue,
			addressLine1Value,
			addressLine2Value,
			dwellingType
		]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Cash Out Refinance Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Client's Current Mortgage Interest Rate: ${currentInterestRate}`,
				`Client's remaining term of current loan ${remainingTerm}`,
				`Client's Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`,
				`Client's Estimated Home Value: ${dollarFormatter.format(
					+estimatedPurchaseValue
				)}`,
				`Client's Current Mortgage Outstanding Balance: ${dollarFormatter.format(
					+currentOutstandingBalance
				)}`,
				`Client's Current Loan Tenure: ${ownershipTenure}`
			],
			additionalNotes,
			lendingTypeRequested: 'Cash Out Refinance'
		});
		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAddressLine1,
						setAddressLine2,
						setCity,
						setState,
						setZipCode,
						setLoanAmount,
						setOwnershipTenure,
						setEstimatedPurchaseValue,
						setOutstandingBalance,
						setAdditionalNotes,
						setEmploymentStatus,
						setPrimaryUse,
						setPropertyType
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			primaryUse,
			propertyType,
			addressLine1,
			addressLine2,
			city,
			clientId,
			currentInterestRate: +currentInterestRate,
			employmentStatus,
			estimatedPurchaseValue: +estimatedPurchaseValue,
			firstName,
			lastName,
			offerType: CASHOUT_OFFER_TYPE,
			outstandingBalance,
			remainingTerm,
			ownershipTenure,
			loanAmount,
			state,
			tradelineType: MORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			primaryUse,
			propertyType,
			city,
			clientId,
			currentInterestRate,
			addressLine1,
			addressLine2,
			employmentStatus,
			estimatedPurchaseValue,
			firstName,
			lastName,
			offerType: CASHOUT_OFFER_TYPE,
			outstandingBalance,
			remainingTerm,
			ownershipTenure,
			loanAmount,
			state,
			tradelineType: MORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAddressLine1,
						setAddressLine2,
						setCity,
						setState,
						setZipCode,
						setLoanAmount,
						setOwnershipTenure,
						setEstimatedPurchaseValue,
						setOutstandingBalance,
						setAdditionalNotes,
						setEmploymentStatus,
						setPropertyType,
						setPrimaryUse
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: outstandingBalance,
				label: 'Current Mortgage Outstanding Balance:'
			},
			{
				type: 'currency',
				value: estimatedPurchaseValue,
				label: 'Estimated Home Value:'
			},
			{
				type: 'currency',
				value: currentInterestRate,
				label: 'Current mortgage interest rate:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired Cash Out Refinance Amount:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Remaining loan term: ${remainingTerm}
				Desired loan term: ${ownershipTenure}
				Property Type: ${propertyType}
				Primary Use: ${primaryUse}
				Employment Status: ${employmentStatus}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: \n${additionalNotes}`;

		const isOutstandingBalancePresent = outstandingBalance !== '';
		const isLoanAmountPresent = loanAmount !== '';

		const updatedFinalAmount = getFinalAmount(
			isOutstandingBalancePresent,
			isLoanAmountPresent,
			outstandingBalance,
			loanAmount
		);

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: updatedFinalAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest(loanRequestId);
		}
	};

	const isLoading = savingLoanInquiry || sendGenericEmail.isLoading;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Cash Out Refinance Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Type of property input */}
						<PropertyTypeDropdown
							selected={propertyType}
							onChange={setPropertyType}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Primary use of property */}
						<Dropdown
							items={TYPES_OF_HOME_PROPERTIES}
							selected={primaryUse}
							onChange={setPrimaryUse}
							variant="outlined"
							label="Property use *"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Current Mortgage Outstanding Balance */}
						<OutstandingBalanceInput
							label="Current Mortgage Outstanding Balance *"
							outstandingBalance={outstandingBalance}
							setOutstandingBalance={setOutstandingBalance}
							inputProps={{
								'data-test': 'outstandingBalance'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Estimated Purchase Value */}
						<CurrencyTextInput
							label="Estimated Purchase Value *"
							subLabel="Estimated price of home"
							value={estimatedPurchaseValue}
							onChange={setEstimatedPurchaseValue}
							inputProps={{
								'data-test': 'estimatedPurchaseValue'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Current Loan Interest Rate */}
						<RateInput
							rate={currentInterestRate}
							setRate={setCurrentInterestRate}
							label="Current mortgage interest rate *"
							inputProps={{
								'data-test': 'interestRateCurrentMortgage'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Current Loan Remaining term */}
						<TextInput
							type={NUMBER}
							label={
								atLoanRequestDetailsUrl
									? 'Remaining loan term *'
									: 'Remaining loan term'
							}
							subLabel="Length of loan in years"
							onChange={setRemainingTerm}
							value={remainingTerm}
							inputProps={{
								'data-test': 'Remaining loan term'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Loan Tenure */}
						<Dropdown
							items={MORTGAGE_LOAN_TENURE_OPTIONS}
							selected={ownershipTenure}
							onChange={setOwnershipTenure}
							variant="outlined"
							label="Desired loan term *"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Desired Amount */}
						<CurrencyTextInput
							label={
								atLoanRequestDetailsUrl
									? 'Desired Cash Out Refinance Amount *'
									: 'Desired Cash Out Refinance Amount'
							}
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'desiredLoanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employment Status */}
						<Dropdown
							items={EMPLOYMENT_STATUS_OPTIONS}
							selected={employmentStatus}
							onChange={setEmploymentStatus}
							variant="outlined"
							label="Employment status"
						/>
					</Grid>

					<GetFullAddressPartialForm
						isRequired
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
						disableAutoComplete={disableAutoComplete}
						setDisableAutoComplete={setDisableAutoComplete}
					/>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

export default CashOutRefiForm;
